import React, { useState } from 'react';
import { Navigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Bug, AlertTriangle, HelpCircle, Lightbulb, CheckCircle2, ChevronDown, ChevronRight, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';

/**
 * /report-problem — in-app pilot triage ("Report a problem").
 *
 * Any logged-in user can raise a ticket (bug / complaint / question / idea).
 * Tickets land in support_tickets (migrate-v20), ping Marakin staff by email,
 * and are triaged in platform admin → Support. The member sees their own
 * tickets + statuses + resolution notes below the form, so reports never feel
 * like they vanished into a black hole.
 */

const CATEGORIES = [
  { key: 'bug', label: 'Something is broken', icon: Bug },
  { key: 'complaint', label: 'Complaint', icon: AlertTriangle },
  { key: 'question', label: 'Question', icon: HelpCircle },
  { key: 'idea', label: 'Idea / suggestion', icon: Lightbulb },
];

const STATUS_META = {
  open: { label: 'Open', bg: 'rgba(196,148,58,0.15)', fg: '#8A6220' },
  in_progress: { label: 'In progress', bg: 'rgba(74,111,165,0.15)', fg: '#3A5A8C' },
  resolved: { label: 'Resolved', bg: 'rgba(30,74,53,0.12)', fg: '#1E4A35' },
  closed: { label: 'Closed', bg: 'rgba(0,0,0,0.07)', fg: '#777' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{
      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: m.bg, color: m.fg, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

export default function ReportProblemPage() {
  const { user, loading } = useAuth();
  const params = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const [searchParams] = useSearchParams();
  // Club-scoped route (/:clubSlug/report-problem) is preferred — it gives the
  // navbar full club context (Hub, Kit Shop, account menu). The bare
  // /report-problem?club= form remains for club-less contexts (deep links).
  const clubSlug = params.clubSlug || searchParams.get('club') || null;
  const qc = useQueryClient();

  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
    enabled: !!clubSlug,
  });

  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyError, setReplyError] = useState(null);

  const { data: tickets } = useQuery({
    queryKey: ['my-support-tickets'],
    queryFn: () => axios.get('/me/support-tickets').then(r => r.data),
    enabled: !!user,
  });

  const submit = useMutation({
    mutationFn: () => axios.post('/me/support-tickets', {
      category,
      message,
      clubSlug,
      context: {
        page: document.referrer ? document.referrer.slice(0, 300) : window.location.href,
        platform: window.Capacitor?.getPlatform?.() || 'web',
        userAgent: navigator.userAgent,
      },
    }).then(r => r.data),
    onSuccess: () => {
      setMessage('');
      setSubmitted(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['my-support-tickets'] });
      setTimeout(() => setSubmitted(false), 6000);
    },
    onError: (e) => setError(e.response?.data?.error || 'Something went wrong — please try again.'),
  });

  const reply = useMutation({
    mutationFn: ({ id, body }) => axios.post(`/me/support-tickets/${id}/messages`, { body }).then(r => r.data),
    onSuccess: (_, { id }) => {
      setReplyDrafts(d => ({ ...d, [id]: '' }));
      setReplyError(null);
      qc.invalidateQueries({ queryKey: ['my-support-tickets'] });
    },
    onError: (e) => setReplyError(e.response?.data?.error || 'Could not send — please try again.'),
  });

  if (!loading && !user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      {club
        ? <Navbar club={club} />
        : <Navbar portalName="Report a problem" portalHome="/my-clubs" />}
      <PageHeader
        icon={AlertCircle}
        title="Report a problem"
        subtitle={<>Spotted a bug or got a question? It goes straight to the Marakin team — track the reply here.</>}
      />

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 20px 80px' }}>
        {submitted && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
            borderRadius: 10, background: 'rgba(30,74,53,0.1)', color: '#1E4A35',
            fontWeight: 600, fontSize: 14.5, marginBottom: 20,
          }}>
            <CheckCircle2 size={18} /> Thanks — we've got it{submit.data?.ticket_no ? ` (ticket #${submit.data.ticket_no})` : ''}. We'll update the status below as we work on it.
          </div>
        )}

        <div className="form-group">
          <label className="label">What kind of report is it?</label>
          <div className="pill-group">
            {CATEGORIES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`pill-btn ${category === key ? 'active' : ''}`}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="label">What happened?</label>
          <textarea
            className="input"
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="The more detail the better — what were you doing, what did you expect, what happened instead?"
            style={{ resize: 'vertical', minHeight: 110, lineHeight: 1.5 }}
            maxLength={5000}
          />
        </div>

        {error && (
          <p style={{ color: '#A33', fontSize: 13.5, fontWeight: 600, marginTop: 8 }}>{error}</p>
        )}

        <button
          className="btn btn-primary"
          style={{ marginTop: 14 }}
          disabled={submit.isPending || message.trim().length < 5}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Sending…' : 'Send report'}
        </button>

        {tickets?.length > 0 && (
          <div style={{ marginTop: 44 }}>
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>My reports</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tickets.map(t => {
                const open = expandedId === t.id;
                return (
                  <div
                    key={t.id}
                    style={{
                      border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10,
                      padding: '12px 14px', cursor: 'pointer', background: 'rgba(255,255,255,0.5)',
                    }}
                    onClick={() => setExpandedId(open ? null : t.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#999', flexShrink: 0 }}>#{t.ticket_no}</span>
                      <span style={{
                        flex: 1, fontSize: 14, fontWeight: 600,
                        whiteSpace: open ? 'normal' : 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {t.message}
                      </span>
                      <StatusBadge status={t.status} />
                    </div>
                    {open && (
                      <div
                        style={{ marginTop: 10, paddingLeft: 25, fontSize: 13.5, color: 'var(--text-secondary)' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <div>Sent {new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>

                        {/* Conversation thread */}
                        {(t.messages || []).map(m => (
                          <div
                            key={m.id}
                            style={{
                              marginTop: 8, padding: '10px 12px', borderRadius: 8,
                              background: m.isStaff ? 'rgba(30,74,53,0.07)' : 'rgba(196,148,58,0.09)',
                              color: 'var(--text-primary)',
                            }}
                          >
                            <strong style={{ color: m.isStaff ? '#1E4A35' : '#8A6220' }}>
                              {m.isStaff ? 'Marakin' : 'You'}:
                            </strong>{' '}
                            {m.body}
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 3 }}>
                              {new Date(m.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))}

                        {/* Reply box — thread stays open until the ticket is closed */}
                        {t.status !== 'closed' ? (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
                            <textarea
                              className="input"
                              rows={2}
                              value={replyDrafts[t.id] || ''}
                              placeholder="Reply to the Marakin team…"
                              onChange={e => setReplyDrafts(d => ({ ...d, [t.id]: e.target.value }))}
                              style={{ flex: 1, resize: 'vertical', lineHeight: 1.45, fontSize: 13.5 }}
                              maxLength={5000}
                            />
                            <button
                              className="btn btn-primary"
                              style={{ padding: '9px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                              disabled={reply.isPending || !(replyDrafts[t.id] || '').trim()}
                              onClick={() => reply.mutate({ id: t.id, body: replyDrafts[t.id] })}
                            >
                              <Send size={14} /> {reply.isPending ? 'Sending…' : 'Send'}
                            </button>
                          </div>
                        ) : (
                          <p style={{ marginTop: 10, fontSize: 12.5, fontStyle: 'italic' }}>
                            This ticket is closed. If the problem comes back, raise a new report above.
                          </p>
                        )}
                        {replyError && expandedId === t.id && (
                          <p style={{ color: '#A33', fontSize: 13, fontWeight: 600, marginTop: 6 }}>{replyError}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
